import React, { useEffect, useState } from 'react';

type SeverityType = 'error' | 'warning' | 'info';

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: string;
  title?: string;
  severity?: SeverityType;
  showRetry?: boolean;
  onRetry?: () => void;
  guidance?: string;
}

/**
 * エラーモーダルダイアログコンポーネント
 * 重要なエラー時に使用し、ユーザーの確認が必要な場合に表示
 */
const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  error,
  title = 'エラーが発生しました',
  severity = 'error',
  showRetry = false,
  onRetry,
  guidance
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => setIsVisible(true), 10);
    } else {
      document.body.style.overflow = 'unset';
      setIsVisible(false);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const getIcon = (): string => {
    switch (severity) {
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'error':
      default:
        return '❌';
    }
  };

  const getModalClassName = (): string => {
    let className = `error-modal-overlay ${severity}`;
    if (isVisible) className += ' error-modal-visible';
    return className;
  };

  if (!isOpen) return null;

  return (
    <div
      className={getModalClassName()}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-modal-title"
      aria-describedby="error-modal-description"
    >
      <div className="error-modal-content">
        <div className="error-modal-header">
          <div className="error-modal-title-container">
            <span className="error-modal-icon" role="img" aria-label={severity}>
              {getIcon()}
            </span>
            <h2 id="error-modal-title" className="error-modal-title">
              {title}
            </h2>
          </div>
          <button className="error-modal-close" onClick={onClose} aria-label="モーダルを閉じる">
            ×
          </button>
        </div>

        <div className="error-modal-body">
          <div id="error-modal-description" className="error-modal-message">
            {error}
          </div>

          {guidance && (
            <div className="error-modal-guidance">
              <div className="error-modal-guidance-title">
                <span className="error-modal-guidance-icon">💡</span>
                <strong>解決方法</strong>
              </div>
              <div className="error-modal-guidance-content">{guidance}</div>
            </div>
          )}
        </div>

        <div className="error-modal-footer">
          {showRetry && onRetry && (
            <button className="button secondary" onClick={onRetry} style={{ marginRight: '10px' }}>
              再試行
            </button>
          )}
          <button className="button" onClick={onClose} autoFocus>
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
